import React, { useState } from 'react';


import PKDCalendar from 'components/PKDCalendar';
import { ViewMoreLink } from 'components/common/ViewMoreLink';
import { SelectInput, menuProps } from 'components/Input/SelectInput.styles';
import { ReactComponent as ArrowLeft } from 'images/components/arrow-left.svg';
import { getHighlightedDays } from 'pages/PersonalCalendar/utils';
import { EmptyContainer } from 'shared-components/EmptyContainer';
import { useGetDailySurveysQuery } from 'store/api/pkd/endpoints/dailySurvey';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import Legend from './Legend';
import PersonalImpactCalendarSkeleton from './PersonalImpactCalendarSkeleton';
import {
  PersonalImpactCalendarWrapper,
  Header,
  Title,
  ImpactDuration,
  Content,
} from './styles';
import {
  colorMapping,
  impactTypeOptions,
  keyMapping,
  labelMapping,
} from './util';
import { StyedMenuItem } from 'shared-components/Input/styles';
import useParticipantTask from 'hooks/useParticipantTask';
import { useGetParticipantTaskQuery } from 'store/api/pkd/endpoints/participantTask';
import { Box } from '@mui/material';
import { TaskStatus } from 'utils/enum';

export const PersonalImpactCalendar = () => {
  const { isPersonLoading, personId, personAccountProfileId } =
    useGetPersonAndAttributes();

  const { dailySurvey, isDailySurveyLoading } = useGetDailySurveysQuery(
    {
      personAccountProfileId,
      params : {
        status  : TaskStatus.Completed,
        include : 'last-participant-task-entry',
      },
    },
    {
      skip             : !personAccountProfileId,
      selectFromResult : ({ data, isFetching, isSuccess, isLoading }) => {
        const dailySurvey = data?.[0]?.data;

        return {
          dailySurvey          : dailySurvey,
          status               : dailySurvey?.status,
          isDailySurveyLoading : isFetching || isLoading,
          isDailySurveySuccess : isSuccess,
        };
      },
    }
  );

  const taskEntries = [dailySurvey?.latestParticipantTaskEntry ?? {}];

  const isLoading = isPersonLoading || isDailySurveyLoading;

  const [impactType, setImpactType] = useState(impactTypeOptions[0].value);
  const handleChange = (event) => setImpactType(event.target.value);

  const hasDailySurveyTaskEntries = taskEntries.every(
    (entry) => Object.keys(entry).length > 0
  );

  const filteredDailySurveyTaskEntries = taskEntries?.filter((taskEntry) => {
    const currentDate = new Date();
    const taskEntryDate = new Date(taskEntry.createdAt);
    return (
      currentDate.getMonth() === taskEntryDate.getMonth() &&
      currentDate.getFullYear() === taskEntryDate.getFullYear()
    );
  });

  const highlightedDays = getHighlightedDays(filteredDailySurveyTaskEntries);

  return (
    <PersonalImpactCalendarWrapper
      sx={{
        padding : {
          xs : '20px 10px',
          md : '20px 30px',
        },
        maxWidth : { md: '30%' },
      }}>
      <Header>
        {hasDailySurveyTaskEntries && (
          <>
            <Box
              justifyContent='space-between'>
              <Title
                variant='h6'>Personal Impact Calendar</Title>
              {hasDailySurveyTaskEntries && (
                <ViewMoreLink
                  to='personal-calendar'>See details</ViewMoreLink>
              )}
            </Box>
            <ImpactDuration
              variant='pl3'>Last 30 days</ImpactDuration>
          </>
        )}
      </Header>
      <Content>
        {isLoading
          ? (
            <PersonalImpactCalendarSkeleton />
          )
          : (
            <>
              {hasDailySurveyTaskEntries
                ? (
                  <>
                    <SelectInput
                      label='Select'
                      menuProps={menuProps}
                      value={impactType}
                      onChange={handleChange}>
                      {impactTypeOptions.map(({ value, label }) => (
                        <StyedMenuItem
                          key={`${value}_${label}`}
                          label={label} value={value}>
                          {label}
                        </StyedMenuItem>
                      ))}
                    </SelectInput>
                    <PKDCalendar
                      month={new Date().getMonth() + 1}
                      year={new Date().getFullYear()}
                      impactType={impactType}
                      highlightedDays={highlightedDays}
                      colorMapping={colorMapping}
                      keyMapping={keyMapping}
                    />
                    <Legend
                      labelMapping={labelMapping[impactType]}
                      colorMapping={colorMapping[impactType]}
                    />
                  </>
                )
                : (
                  <EmptyContainer
                    title='No data yet'
                    description='Start by filling daily survey so we can show how your data is making an impact.'
                    renderCta={<ArrowLeft />}
                  />
                )}
            </>
          )}
      </Content>
    </PersonalImpactCalendarWrapper>
  );
};
